import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileCheck2, ChevronLeft, Search, ExternalLink, RotateCcw,
  Calendar, Download, Copy, Check, ShieldCheck, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import SynapseLoader from "@/components/SynapseLoader";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "oggi";
    if (days === 1) return "ieri";
    if (days < 30) return `${days} giorni fa`;
    if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
    return `${Math.floor(days / 365)} anni fa`;
  } catch { return ""; }
}

export default function StoricoRefertiPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSpecialist = user?.profession === "medico_specialista" || user?.profession === "medico_medicina_generale" || user?.profession === "biologo_nutrizionista";

  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/referto/history", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Impossibile caricare lo storico");
    } finally { setLoading(false); }
  }, [q, dateFrom, dateTo]);

  const hasFilters = q.trim() || dateFrom || dateTo;

  // Nessun caricamento all'apertura — mostra risultati SOLO dopo una ricerca
  useEffect(() => {
    if (!isSpecialist) return;
    if (!hasFilters) {
      setItems([]); setTotal(0); setLoading(false);
      return;
    }
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [q, dateFrom, dateTo, isSpecialist, hasFilters, load]);

  const resetFilters = () => { setQ(""); setDateFrom(""); setDateTo(""); };

  const copyLink = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    } catch { toast.error("Impossibile copiare"); }
  };

  const isExpired = (iso) => {
    try { return new Date(iso).getTime() < Date.now(); }
    catch { return false; }
  };

  if (!isSpecialist) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="p-8 text-center text-slate-600 bg-white">
          Questa pagina è riservata ai medici specialisti.
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto pb-24" data-testid="storico-referti-page">
      <button onClick={() => navigate("/dashboard")}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-3">
        <ChevronLeft className="h-4 w-4" /> Dashboard
      </button>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <FileCheck2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Storico referti</h1>
            <p className="text-sm text-slate-500">Tutti i referti PDF che hai emesso, cercabili per paziente e periodo.</p>
          </div>
        </div>
        <Button onClick={() => navigate("/nuovo-referto")}
                data-testid="storico-new-referto"
                className="h-10 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981] hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669] text-white font-semibold">
          <FileCheck2 className="h-4 w-4 mr-2" /> Nuovo referto
        </Button>
      </div>

      {/* Filtri */}
      <Card className="p-4 rounded-2xl border-slate-200 shadow-sm bg-white mb-4
                       [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                       [&_input]:border-slate-300">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label>Cerca paziente</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Nome, cognome o email…" className="pl-9"
                     data-testid="storico-search" />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Da</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   data-testid="storico-date-from" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> A</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   data-testid="storico-date-to" />
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {total > 0 ? `${total} refert${total === 1 ? "o" : "i"} trovat${total === 1 ? "o" : "i"}` : "Nessun risultato"}
            </div>
            <Button variant="ghost" size="sm" onClick={resetFilters}
                    data-testid="storico-reset"
                    className="text-slate-600 hover:text-slate-900">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Azzera filtri
            </Button>
          </div>
        )}
      </Card>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <SynapseLoader size="lg" label="Carico storico…" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center bg-white border-slate-200">
          <FileCheck2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-semibold text-slate-700">
            {hasFilters ? "Nessun referto corrisponde ai filtri" : "Cerca un referto"}
          </div>
          <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {hasFilters
              ? "Prova a modificare i filtri o azzerarli."
              : "Per motivi di riservatezza, i referti non sono elencati automaticamente. Inserisci nome, cognome, email della paziente o un intervallo di date per rintracciare il referto."}
          </div>
          {!hasFilters && (
            <Button onClick={() => navigate("/nuovo-referto")}
                    className="mt-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white">
              <FileCheck2 className="h-4 w-4 mr-2" /> Crea nuovo referto
            </Button>
          )}
        </Card>
      ) : (
        <Card className="rounded-2xl border-slate-200 shadow-sm bg-white overflow-hidden">
          <div className="divide-y divide-slate-100" data-testid="storico-list">
            {items.map((r) => {
              const expired = isExpired(r.expires_at);
              return (
                <div key={r.referto_id} className="p-4 md:px-5 md:py-4 flex flex-col md:flex-row md:items-center gap-3 hover:bg-slate-50 transition-colors"
                     data-testid={`storico-item-${r.referto_id}`}>
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    expired ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-600"
                  }`}>
                    <FileCheck2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 truncate">{r.patient_name || "—"}</div>
                      {r.thread_id && (
                        <Badge variant="secondary" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                          <MessageSquare className="h-3 w-3 mr-0.5" /> con sinapsi
                        </Badge>
                      )}
                      {expired && (
                        <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">Scaduto</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.patient_email || "—"}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                      <span title={r.created_at}>Emesso {fmtRelative(r.created_at)} · {fmtDate(r.created_at)}</span>
                      <span>Download: <strong className="text-slate-600">{r.downloads || 0}</strong></span>
                      {r.content_hash && (
                        <span className="font-mono flex items-center gap-1" title={`Hash SHA-256: ${r.content_hash}`}>
                          <ShieldCheck className="h-3 w-3 text-emerald-500" />
                          {r.content_hash.slice(0, 8)}…
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.thread_id && (
                      <Button variant="outline" size="sm" onClick={() => navigate(`/messaggi/${r.thread_id}`)}
                              className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9"
                              data-testid={`storico-thread-${r.referto_id}`}>
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> Sinapsi
                      </Button>
                    )}
                    <Button variant="outline" size="sm"
                            onClick={() => copyLink(r.download_url, r.referto_id)}
                            disabled={expired}
                            className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 disabled:opacity-40"
                            data-testid={`storico-copy-${r.referto_id}`}>
                      {copied === r.referto_id
                        ? <><Check className="h-3.5 w-3.5 mr-1" /> Copiato</>
                        : <><Copy className="h-3.5 w-3.5 mr-1" /> Link</>}
                    </Button>
                    <a href={expired ? undefined : r.download_url}
                       target="_blank" rel="noopener noreferrer"
                       onClick={(e) => expired && e.preventDefault()}
                       className={`inline-flex items-center gap-1 px-3 h-9 rounded-md text-sm font-semibold ${
                         expired ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                 : "bg-emerald-600 text-white hover:bg-emerald-700"
                       }`}
                       data-testid={`storico-download-${r.referto_id}`}>
                      <Download className="h-3.5 w-3.5" /> PDF
                      <ExternalLink className="h-3 w-3 opacity-70" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          {total > items.length && (
            <div className="p-3 text-center text-xs text-slate-500 border-t border-slate-100 bg-slate-50">
              Mostrati {items.length} di {total} referti. Restringi il filtro data per vedere i più recenti.
            </div>
          )}
        </Card>
      )}

      <p className="text-[11px] text-slate-400 italic text-center mt-6">
        I link ai PDF scadono 30 giorni dopo l'emissione. Passata questa data il paziente non può più scaricare il documento.
      </p>
    </div>
  );
}
